import {
  type ActorReplicaExport,
  type ActorSyncExportOptions,
  exportActorReplica,
} from "#export-actor-sync-changes";
import { nativeDatabaseGenerations } from "#native-database-generations";
import { openOwnedTursoGeneration } from "#open-owned-turso-generation";

/**
 * What a replica about to be replaced still holds for the cloud (see `database.md`, "Device:
 * sync supervisor and rebuild"), read with the connection CLOSED.
 *
 * The file is opened as a plain database, without the sync machinery: a rebuild is the response
 * to a sync engine that keeps failing on this file, so the export must not depend on that engine
 * connecting. Nothing is written.
 */
export async function readActorReplicaExport(
  databaseName: string,
  options: ActorSyncExportOptions = {},
): Promise<ActorReplicaExport> {
  const { Database } = await import("@-/com.npmjs.tursodatabase__sync-react-native");
  const { tursoDb } = await openOwnedTursoGeneration(databaseName, async (path, fresh) => {
    if (fresh) {
      // The caller checked that a replica exists; a fresh candidate here would be an empty file
      // published as the account's replica.
      throw new Error(`${databaseName}: there is no replica to read for the rebuild`);
    }
    return new Database({ path });
  });
  try {
    return await exportActorReplica(tursoDb, {
      ...options,
      name: nativeActorReplicaName(databaseName),
    });
  } finally {
    await tursoDb.close();
  }
}

/** The generation that holds the replica, which names its imports: `.../<generation>/actor.db`. */
export function nativeActorReplicaName(databaseName: string): string {
  const path = nativeDatabaseGenerations.peek(databaseName);
  return path?.split("/").at(-2) ?? databaseName;
}
